import { CSV_IMPORT_KEYS } from '../config/csvImportConfig';
import { createApiResponse } from '../models';
import { normalizeBatchUpsertResult } from '../models/supplyChainBatchModels';
import { buildBatchPayloadFromMappedRows } from '../utils/importDtoMappers';
import { createUserApiClient } from './authenticatedApiClient';
import { getStoredUserId } from './authSession';

const createBatchClient = (userId) => createUserApiClient(userId, 'batch');

const withBatchClient = async (userId, requestHandler) => {
  const uid = userId || getStoredUserId();
  if (!uid) {
    return createApiResponse({ success: false, message: 'User not authenticated' });
  }
  try {
    const client = createBatchClient(uid);
    return await requestHandler(client);
  } catch (error) {
    const data = normalizeBatchUpsertResult(error.response?.data?.data || {});
    return createApiResponse({
      success: false,
      message: error.response?.data?.message || error.message || 'Batch upload failed',
      errorCode: error.response?.data?.errorCode || null,
      data,
    });
  }
};

const postBatch = async (client, path, body) => {
  const response = await client.post(path, body);
  const apiResponse = createApiResponse(response.data);
  if (apiResponse.success) {
    apiResponse.data = normalizeBatchUpsertResult(apiResponse.data || {});
  }
  return apiResponse;
};

const ENDPOINT_BY_KEY = {
  [CSV_IMPORT_KEYS.tenantCompany]: '/tenant-company',
  [CSV_IMPORT_KEYS.facilities]: '/tenant-facilities',
  [CSV_IMPORT_KEYS.suppliers]: '/suppliers',
  [CSV_IMPORT_KEYS.supplierFacilities]: '/supplier-facilities',
  [CSV_IMPORT_KEYS.commodities]: '/commodities',
  [CSV_IMPORT_KEYS.geographies]: '/geographies',
  [CSV_IMPORT_KEYS.logistics]: '/logistics',
  [CSV_IMPORT_KEYS.products]: '/products',
  [CSV_IMPORT_KEYS.graphOverview]: '/graph-relationships',
};

export const supplyChainBatchService = {
  CSV_IMPORT_KEYS,

  uploadByImportKey(importKey, mappedRows, userId) {
    const built = buildBatchPayloadFromMappedRows(importKey, mappedRows);
    const path = ENDPOINT_BY_KEY[importKey];
    if (!path) {
      return Promise.resolve(
        createApiResponse({ success: false, message: 'Unsupported import type' })
      );
    }

    if (built.kind === 'single') {
      if (!built.payload) {
        return Promise.resolve(
          createApiResponse({ success: false, message: 'No valid tenant company row to import' })
        );
      }
      return withBatchClient(userId, (client) => postBatch(client, path, built.payload));
    }

    const list = Array.isArray(built.payload) ? built.payload : [];
    if (!list.length) {
      return Promise.resolve(
        createApiResponse({ success: false, message: 'No valid rows to import after mapping' })
      );
    }
    return withBatchClient(userId, (client) => postBatch(client, path, list));
  },

  batchSuppliers(suppliers, userId) {
    return withBatchClient(userId, (client) => postBatch(client, '/suppliers', suppliers));
  },
};
